import React, { ReactNode } from 'react'
import { useElementVisibility } from 'hooks/useElementVisibility'

interface LazyFlightCardProps {
  children: ReactNode
  /** Kart indeksi (0'dan başlar) */
  index: number
  /** Mobil cihaz kontrolü */
  isMobile: boolean
  /** Preload edilecek mi (ilk N kart için true) */
  preload?: boolean
  /** Test ID'si */
  dataTestId?: string
}

/**
 * Uçuş kartları için lazy loading wrapper bileşeni
 * Desktop'ta ilk 4, mobil'de ilk 2 kart preload edilir
 * Geri kalanlar görünür olduğunda yüklenir
 */
const LazyFlightCard: React.FC<LazyFlightCardProps> = ({ children, index, isMobile, preload = false, dataTestId = 'lazy-flight-card' }) => {
  // Preload edilecek kart sayısını belirle
  const preloadLimit = isMobile ? 2 : 4
  const shouldPreload = preload || index < preloadLimit

  // useElementVisibility hook'unu kullan (sadece preload edilmeyenler için)
  const [cardRef, isVisible] = useElementVisibility({
    isMobileDevice: isMobile,
    disconnectOnVisible: true,
    threshold: isMobile ? 0.1 : 0.01,
    rootMargin: isMobile ? '50px' : '200px',
    initDelay: 100,
  })

  // Preload edilecek kartlar veya görünür olan kartlar için içeriği göster
  const shouldRender = shouldPreload || isVisible
  const placeholderMinHeight = isMobile ? '200px' : '250px'

  return (
    <>
      {/* CSS animasyonu için style */}
      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        .spinner {
          animation: spin 1s linear infinite;
        }
      `}</style>

      <div
        ref={shouldPreload ? undefined : cardRef}
        data-testid={`${dataTestId}-${index}`}
        data-visible={shouldRender ? 'true' : 'false'}
        data-preload={shouldPreload ? 'true' : 'false'}
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: shouldRender ? '100%' : undefined,
          minHeight: shouldRender ? 'auto' : placeholderMinHeight,
          transition: 'all 0.3s ease-in-out',
        }}
      >
        {shouldRender ? (
          children
        ) : (
          // Placeholder görünümü
          <div
            style={{
              width: '100%',
              height: isMobile ? '200px' : '250px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #e0e0e0',
            }}
          >
            <div
              className="spinner"
              style={{
                width: '40px',
                height: '40px',
                border: '3px solid #e0e0e0',
                borderTop: '3px solid #0066cc',
                borderRadius: '50%',
              }}
            />
          </div>
        )}
      </div>
    </>
  )
}

export default LazyFlightCard
